<template>
  <div
    class="mention-item"
    :class="['mention-item--' + type, { 'mention-item--expanded': isExpanded }]"
    @click="handleClick"
  >
    <div class="mention-item__header">
      <div class="mention-item__avatar" :style="{ backgroundColor: item.avatarBg || '#E0E0E0' }">
        <span class="mention-item__initials" :style="{ color: item.avatarColor || '#9A9A9A' }">{{ item.initials }}</span>
      </div>
      <div class="mention-item__name-wrap">
        <span class="mention-item__name">{{ item.name }}</span>
        <span v-if="item.isNew" class="mention-item__new-dot" />
      </div>
      <span class="mention-item__time">{{ item.timeAgo }}</span>
    </div>
    <div v-if="item.patientName" class="mention-item__patient">
      <span class="mention-item__patient-label">Patient:</span>
      <span class="mention-item__patient-name">{{ item.patientName }}</span>
    </div>
    <p v-if="item.message" class="mention-item__message" v-html="formattedMessage" />

    <!-- Expanded section with slide animation -->
    <transition
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
    >
      <div v-if="isExpanded" class="mention-item__expandable">
      <!-- Action buttons row -->
      <div class="mention-item__actions">
        <template v-if="loadingReplies">
          <div class="mention-item__shimmer" />
        </template>
        <template v-else>
        <button
          v-if="replies.length > 0"
          class="mention-item__action-btn"
          :class="{ 'mention-item__action-btn--active': showComments }"
          @click.stop="toggleComments"
        >
          <svg v-if="showComments" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 5.9748C15.79 5.9748 19.17 8.1048 20.82 11.4748C20.23 12.6948 19.4 13.7448 18.41 14.5948L19.82 16.0048C21.21 14.7748 22.31 13.2348 23 11.4748C21.27 7.08481 17 3.9748 12 3.9748C10.73 3.9748 9.51 4.1748 8.36 4.5448L10.01 6.1948C10.66 6.0648 11.32 5.9748 12 5.9748ZM10.93 7.1148L13 9.1848C13.57 9.4348 14.03 9.8948 14.28 10.4648L16.35 12.5348C16.43 12.1948 16.49 11.8348 16.49 11.4648C16.5 8.9848 14.48 6.9748 12 6.9748C11.63 6.9748 11.28 7.0248 10.93 7.1148ZM2.01 3.8448L4.69 6.52481C3.06 7.8048 1.77 9.5048 1 11.4748C2.73 15.8648 7 18.9748 12 18.9748C13.52 18.9748 14.98 18.6848 16.32 18.1548L19.74 21.5748L21.15 20.1648L3.42 2.4248L2.01 3.8448ZM9.51 11.3448L12.12 13.9548C12.08 13.9648 12.04 13.9748 12 13.9748C10.62 13.9748 9.5 12.8548 9.5 11.4748C9.5 11.4248 9.51 11.3948 9.51 11.3448ZM6.11 7.9448L7.86 9.69481C7.63 10.2448 7.5 10.8448 7.5 11.4748C7.5 13.9548 9.52 15.9748 12 15.9748C12.63 15.9748 13.23 15.8448 13.77 15.6148L14.75 16.5948C13.87 16.8348 12.95 16.9748 12 16.9748C8.21 16.9748 4.83 14.8448 3.18 11.4748C3.88 10.0448 4.9 8.8648 6.11 7.9448Z" fill="#374957"/>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.79 17.5 12 17.5C8.21 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z" fill="#374957"/>
          </svg>
          <span>{{ showComments ? 'Hide Comments' : commentsLabel }}</span>
        </button>
        <span v-if="replies.length > 0" class="mention-item__action-divider">·</span>
        <button
          class="mention-item__action-btn"
          :class="{ 'mention-item__action-btn--active': showReplyInput }"
          @click.stop="toggleReplyInput"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="15" viewBox="0 0 18 15" fill="none">
            <path d="M7 4V0L0 7L7 14V9.9C12 9.9 15.5 11.5 18 15C17 10 14 5 7 4Z" :fill="showReplyInput ? '#E0E0E0' : '#374957'"/>
          </svg>
          <span>Reply</span>
        </button>
        </template>
      </div>

      <!-- Comments list -->
      <transition
        @before-enter="beforeEnterComments"
        @enter="enterComments"
        @after-enter="afterEnterComments"
        @before-leave="beforeLeaveComments"
        @leave="leaveComments"
        @after-leave="afterLeaveComments"
      >
        <div v-if="showComments && replies.length > 0" class="mention-item__comments">
          <div
            v-for="reply in formattedReplies"
            :key="reply.id"
            class="mention-item__comment"
          >
            <div class="mention-item__comment-header">
              <span class="mention-item__comment-author">{{ reply.author }}</span>
              <span class="mention-item__comment-time">{{ reply.time }}</span>
            </div>
            <p class="mention-item__comment-text" v-html="reply.displayHtml" />
          </div>
        </div>
      </transition>

      <!-- Reply input -->
      <div v-if="showReplyInput" class="mention-item__reply-box" @click.stop>
        <input
          v-model="replyText"
          class="mention-item__reply-input"
          placeholder="Write a quick reply..."
          @keydown.enter="sendReply"
        />
        <button
          class="mention-item__reply-send"
          :disabled="!replyText.trim() || sendingReply"
          @click.stop="sendReply"
        >
          Send
        </button>
      </div>

      <!-- Open the notes -->
      <button class="mention-item__jump" @click.stop="jumpToConversation">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="#004879"/>
        </svg>
        <span>Open the notes</span>
      </button>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MentionItem',
  props: {
    item: { type: Object, required: true },
    type: { type: String, default: 'chat' },
    isExpanded: { type: Boolean, default: false },
    customerNameMap: { type: Object, default: () => ({}) },
  },
  emits: ['expand', 'jump-to-conversation'],
  data() {
    return {
      replies: [],
      showComments: false,
      showReplyInput: false,
      replyText: '',
      sendingReply: false,
      loadingReplies: false,
    };
  },
  computed: {
    formattedMessage() {
      if (!this.item.message) return '';
      return this.item.message.replace(
        /<mention>(.*?)<\/mention>/g,
        '<span class="mention-highlight">$1</span>'
      );
    },
    commentsLabel() {
      return this.replies.length === 1 ? '1 Comment' : `${this.replies.length} Comments`;
    },
    formattedReplies() {
      return this.replies.map(reply => {
        const authorId = reply.author_customer_id;
        const authorName = this.customerNameMap[authorId] || `Provider ${authorId}`;
        return {
          id: reply.id,
          author: authorName,
          time: this.formatTime(reply.created_at),
          displayHtml: this.transformMentions(reply.content),
        };
      });
    },
  },
  watch: {
    isExpanded(val) {
      if (val) {
        this.fetchReplies();
        if (this.item.isNew) {
          this.$store.dispatch('notifications/markMentionAsRead', this.item.noteId);
        }
      } else {
        this.showComments = false;
        this.showReplyInput = false;
        this.replyText = '';
      }
    },
  },
  methods: {
    handleClick() {
      if (this.isExpanded) {
        this.$emit('expand', null);
      } else {
        this.$emit('expand', this.item.id);
      }
    },
    async fetchReplies() {
      this.loadingReplies = true;
      try {
        this.replies = await this.$store.dispatch('notifications/fetchMentionReplies', this.item.noteId);
      } catch (e) {
        console.error('fetchReplies error:', e);
        this.replies = [];
      } finally {
        this.loadingReplies = false;
      }
    },
    toggleComments() {
      this.showComments = !this.showComments;
    },
    toggleReplyInput() {
      this.showReplyInput = !this.showReplyInput;
      if (!this.showReplyInput) this.replyText = '';
    },
    async sendReply() {
      if (!this.replyText.trim() || this.sendingReply) return;
      this.sendingReply = true;
      try {
        await this.$store.dispatch('notifications/createMentionReply', {
          noteId: this.item.noteId,
          content: this.replyText,
        });
        this.replyText = '';
        this.showReplyInput = false;
        await this.fetchReplies();
      } catch (e) {
        console.error('sendReply error:', e);
      } finally {
        this.sendingReply = false;
      }
    },
    jumpToConversation() {
      this.$emit('jump-to-conversation', {
        patientId: this.item.patientId,
        noteId: this.item.noteId,
      });
    },
    transformMentions(content) {
      if (!content) return '';
      let safe = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      safe = safe.replace(/@doctor_(\w+)_\w+/g, (_match, customerId) => {
        const name = this.customerNameMap[customerId] || `Provider ${customerId}`;
        return `<span class="mention-highlight">@${name}</span>`;
      });
      return safe;
    },
    /* Comments slide transition hooks */
    beforeEnterComments(el) {
      el.style.height = '0';
      el.style.opacity = '0';
      el.style.overflow = 'hidden';
    },
    enterComments(el, done) {
      void el.offsetHeight;
      el.style.transition = 'height 0.25s ease, opacity 0.25s ease';
      el.style.height = el.scrollHeight + 'px';
      el.style.opacity = '1';
      el.addEventListener('transitionend', done, { once: true });
    },
    afterEnterComments(el) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
    },
    beforeLeaveComments(el) {
      el.style.height = el.scrollHeight + 'px';
      el.style.overflow = 'hidden';
    },
    leaveComments(el, done) {
      void el.offsetHeight;
      el.style.transition = 'height 0.25s ease, opacity 0.25s ease';
      el.style.height = '0';
      el.style.opacity = '0';
      el.addEventListener('transitionend', done, { once: true });
    },
    afterLeaveComments(el) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
    },
    /* Expand/collapse transition hooks */
    beforeEnter(el) {
      el.style.height = '0';
      el.style.opacity = '0';
      el.style.overflow = 'hidden';
    },
    enter(el, done) {
      void el.offsetHeight;
      el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      el.style.height = el.scrollHeight + 'px';
      el.style.opacity = '1';
      el.addEventListener('transitionend', done, { once: true });
    },
    afterEnter(el) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + 'px';
      el.style.overflow = 'hidden';
    },
    leave(el, done) {
      void el.offsetHeight;
      el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      el.style.height = '0';
      el.style.opacity = '0';
      el.addEventListener('transitionend', done, { once: true });
    },
    afterLeave(el) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const noteDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const diffDays = Math.floor((today - noteDate) / (1000 * 60 * 60 * 24));
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      const timeStr = `${hours12}:${minutes} ${ampm}`;
      if (diffDays === 0) return `Today ${timeStr}`;
      if (diffDays === 1) return `Yesterday ${timeStr}`;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} ${timeStr}`;
    },
  },
};
</script>

<style scoped>
.mention-item {
  border-radius: 10px;
  padding: 12px 24px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  transition: box-shadow 0.2s, background 0.2s;
}

.mention-item:hover {
  box-shadow: 0px 4px 12px rgba(26, 32, 44, 0.06);
}

.mention-item--expanded {
  cursor: default;
}

.mention-item__expandable {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mention-item--chat {
  background: #F9FBFD;
  border: 1px solid #81B0E1;
}

.mention-item--chat:hover,
.mention-item--chat.mention-item--expanded {
  background: #eff5fb;
  border-color: #5587bc;
}

.mention-item--internalNotes {
  background: #FEFCF9;
  border: 1px solid #EDCEB6;
}

.mention-item--internalNotes:hover,
.mention-item--internalNotes.mention-item--expanded {
  background: #fff9f4;
  border-color: #ddb392;
}

.mention-item__header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mention-item__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 8px;
  box-sizing: border-box;
}

.mention-item__initials {
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.mention-item__name-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.mention-item__name {
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #374957;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mention-item__new-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #EF4444;
  flex-shrink: 0;
}

.mention-item__time {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0;
}

.mention-item__patient {
  display: flex;
  gap: 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
}

.mention-item__patient-label {
  color: #999;
}

.mention-item__patient-name {
  color: #666;
}

.mention-item__message {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.2px;
  color: #333;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.mention-item--expanded .mention-item__message {
  -webkit-line-clamp: unset;
  display: block;
}

.mention-item__message :deep(.mention-highlight) {
  color: #25B8C2;
  font-weight: 600;
}

/* Action buttons */
.mention-item__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
}

.mention-item__action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #374957;
  padding: 4px 0;
}

.mention-item__action-btn:hover {
  color: #004879;
}

.mention-item__action-btn--active span {
  color: #999;
}

.mention-item__action-divider {
  color: #999;
  font-size: 14px;
}

/* Shimmer loading placeholder */
.mention-item__shimmer {
  width: 140px;
  height: 18px;
  border-radius: 4px;
  background: linear-gradient(90deg, #E8E8E8 25%, #F5F5F5 50%, #E8E8E8 75%);
  background-size: 200% 100%;
  animation: mention-shimmer 1.5s ease-in-out infinite;
}

@keyframes mention-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Comments section */
.mention-item__comments {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
}

.mention-item__comment {
  background: #fff;
  border: 1px solid #E6E6E6;
  border-radius: 8px;
  padding: 10px 12px;
}

.mention-item__comment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.mention-item__comment-author {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #374957;
}

.mention-item__comment-time {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 11px;
  color: #999;
}

.mention-item__comment-text {
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: #333;
  margin: 0;
}

.mention-item__comment-text :deep(.mention-highlight) {
  color: #25B8C2;
  font-weight: 600;
}

/* Reply input */
.mention-item__reply-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
}

.mention-item__reply-input {
  flex: 1;
  border: 1px solid #C4C4C4;
  border-radius: 8px;
  padding: 8px 12px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  outline: none;
  color: #333;
}

.mention-item__reply-input::placeholder {
  color: #999;
}

.mention-item__reply-input:focus {
  border-color: #004879;
}

.mention-item__reply-send {
  background: #004879;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.mention-item__reply-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mention-item__reply-send:not(:disabled):hover {
  background: #003a61;
}

/* Jump to conversation */
.mention-item__jump {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #004879;
  padding: 8px 0 0;
  margin-top: 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.mention-item__jump:hover {
  text-decoration: underline;
}
</style>
